import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';

import '@/lib/pdfWorker';

type Rect = { x: number; y: number; w: number; h: number };

/**
 * Lets the user upload a PDF, draws a region selection rectangle on the
 * rendered page, and emits a PNG data URL of just that region. The white
 * background of the cropped image is made transparent so the extracted
 * signature lays cleanly over other documents.
 *
 * Customer requirement: "pdf-dən extract olunmuş real imza" — instead of
 * full OCR auto-detection (overkill + unreliable), we let the user point
 * to where the signature is. Half-second of work, repeatable result.
 */
export function PdfSignatureExtractor({
  onExtract,
}: {
  onExtract: (dataUrl: string) => void;
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const pageWrapperRef = useRef<HTMLDivElement>(null);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [rect, setRect] = useState<Rect | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [extractedPreview, setExtractedPreview] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);

  // Clean up blob URL when component unmounts or PDF changes.
  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  function handleFile(file: File) {
    if (file.type !== 'application/pdf') return;
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfDoc(null);
    setPdfUrl(URL.createObjectURL(file));
    setRect(null);
    setExtractedPreview(null);
    setPageNum(1);
  }

  function startDrag(e: React.MouseEvent | React.TouchEvent) {
    const pos = mousePos(e, overlayRef.current);
    if (!pos) return;
    e.preventDefault();
    setDragStart(pos);
    setRect({ x: pos.x, y: pos.y, w: 0, h: 0 });
    setExtractedPreview(null);
  }

  function moveDrag(e: React.MouseEvent | React.TouchEvent) {
    if (!dragStart) return;
    const pos = mousePos(e, overlayRef.current);
    if (!pos) return;
    e.preventDefault();
    setRect({
      x: Math.min(dragStart.x, pos.x),
      y: Math.min(dragStart.y, pos.y),
      w: Math.abs(pos.x - dragStart.x),
      h: Math.abs(pos.y - dragStart.y),
    });
  }

  function endDrag() {
    setDragStart(null);
  }

  /**
   * Re-render *only* the selected region straight from the PDF at high DPI
   * instead of cropping the on-screen page canvas. A signature is usually a
   * small part of the page, so cropping the display render leaves too few
   * pixels and the result looks blurry/pixelated. Rendering the region on its
   * own lets us hit ~1600px across the signature regardless of page size —
   * the customer's "PDF-dən götürülən imza daha da aydın olmalıdır".
   *
   * Near-white pixels are then made transparent so the signature overlays
   * other documents without an ugly white block.
   */
  async function extractFromSelection() {
    if (!rect || rect.w < 10 || rect.h < 10) return;
    if (!pdfDoc || !overlayRef.current) return;

    setExtracting(true);
    try {
      const page = await pdfDoc.getPage(pageNum);

      // The overlay box exactly covers the displayed page, so the selection
      // maps to page fractions we can re-apply at any render scale.
      const disp = overlayRef.current.getBoundingClientRect();
      const fx = rect.x / disp.width;
      const fy = rect.y / disp.height;
      const fw = rect.w / disp.width;
      const fh = rect.h / disp.height;

      // Pick a scale so the cropped signature is ~1600px wide (clamped). This
      // is the whole point of the change: a tiny on-page signature is rendered
      // large and crisp rather than upscaled from a few dozen source pixels.
      const base = page.getViewport({ scale: 1 });
      const regionPts = Math.max(1, base.width * fw);
      const scale = Math.min(10, Math.max(3, 1600 / regionPts));
      const viewport = page.getViewport({ scale });

      const cropX = Math.floor(viewport.width * fx);
      const cropY = Math.floor(viewport.height * fy);
      const cropW = Math.max(1, Math.ceil(viewport.width * fw));
      const cropH = Math.max(1, Math.ceil(viewport.height * fh));

      const out = document.createElement('canvas');
      out.width = cropW;
      out.height = cropH;
      const ctx = out.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      // White backdrop first (identity transform), then shift the page so the
      // selected region lands at the canvas origin and render just that slice.
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, cropW, cropH);
      ctx.translate(-cropX, -cropY);
      await page.render({ canvasContext: ctx, viewport }).promise;
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      // Two-pass cleanup for legibility on top of any background:
      //   1. Luminance-based alpha: pixels close to paper-white become fully
      //      transparent; mid-tones get a graded alpha so anti-aliased
      //      stroke edges stay soft instead of jagged.
      //   2. Contrast curve on the surviving pixels — pulls grey ink toward
      //      pure black so the extracted signature reads as crisply as a
      //      drawn one when it lands on a dark or coloured page later.
      try {
        const img = ctx.getImageData(0, 0, cropW, cropH);
        const px = img.data;
        const PAPER = 240;      // anything brighter than this is paper
        const STROKE = 110;     // anything darker than this is ink
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i];
          const g = px[i + 1];
          const b = px[i + 2];
          // BT.709 luminance — better than channel average for ink/paper
          const Y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          if (Y >= PAPER) {
            px[i + 3] = 0;
            continue;
          }
          if (Y <= STROKE) {
            // Force core ink to pure black, full opacity.
            px[i] = 0;
            px[i + 1] = 0;
            px[i + 2] = 0;
            px[i + 3] = 255;
            continue;
          }
          // Mid-tone: linearly map (STROKE..PAPER) → (alpha 255..0) and
          // darken toward black on the way down so faint pen strokes don't
          // wash out when composited.
          const t = (PAPER - Y) / (PAPER - STROKE); // 0..1
          const alpha = Math.round(255 * t);
          const v = Math.round(40 * (1 - t)); // 40..0 — never above mid-grey
          px[i] = v;
          px[i + 1] = v;
          px[i + 2] = v;
          px[i + 3] = alpha;
        }
        ctx.putImageData(img, 0, 0);
      } catch {
        // Tainted canvas — fall through with the opaque crop.
      }

      const dataUrl = out.toDataURL('image/png');
      setExtractedPreview(dataUrl);
      onExtract(dataUrl);
    } finally {
      setExtracting(false);
    }
  }

  if (!pdfUrl) {
    return (
      <div
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed border-border-strong rounded-md p-6 text-center cursor-pointer hover:border-primary hover:bg-primary-50 transition-colors"
      >
        <div className="text-3xl mb-2">📄</div>
        <div className="text-[13px] font-medium text-gray-900">{t('settings_signature.pdf_drop')}</div>
        <div className="text-[11px] text-muted mt-1">{t('settings_signature.pdf_hint')}</div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
            setRect(null);
            setExtractedPreview(null);
          }}
          className="text-[11px] text-muted hover:text-gray-900"
        >
          ↩ {t('settings_signature.choose_different_pdf')}
        </button>
        {numPages > 1 && (
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPageNum((p) => Math.max(1, p - 1))}
              disabled={pageNum <= 1}
              className="px-2 py-0.5 text-[11px] text-muted hover:text-gray-900 disabled:opacity-30"
            >
              ←
            </button>
            <span className="text-[11px] text-muted">
              {pageNum} / {numPages}
            </span>
            <button
              type="button"
              onClick={() => setPageNum((p) => Math.min(numPages, p + 1))}
              disabled={pageNum >= numPages}
              className="px-2 py-0.5 text-[11px] text-muted hover:text-gray-900 disabled:opacity-30"
            >
              →
            </button>
          </div>
        )}
      </div>

      <p className="text-[11px] text-muted mb-2">{t('settings_signature.pdf_instructions')}</p>

      <div
        ref={pageWrapperRef}
        className="relative border border-border rounded-md overflow-hidden inline-block max-w-full"
        style={{ touchAction: 'none' }}
      >
        <Document
          file={pdfUrl}
          onLoadSuccess={(p) => {
            setPdfDoc(p);
            setNumPages(p.numPages);
          }}
          loading={
            <div className="p-12 text-muted text-[12px]">{t('settings_signature.loading_pdf')}</div>
          }
          error={
            <div className="p-12 text-danger text-[12px]">{t('fields_step.failed_to_load_pdf')}</div>
          }
        >
          {/* Display render only (1560 → shown at 520 for a crisp preview on
              hi-dpi screens). Extraction no longer crops this canvas — it
              re-renders the selected region from the PDF at high DPI in
              extractFromSelection(), so display resolution and signature
              sharpness are now decoupled. */}
          <Page
            pageNumber={pageNum}
            width={1560}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            canvasBackground="white"
            className="!w-[520px] [&_canvas]:!w-full [&_canvas]:!h-auto"
          />
        </Document>

        <div
          ref={overlayRef}
          className="absolute inset-0 cursor-crosshair"
          onMouseDown={startDrag}
          onMouseMove={moveDrag}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onTouchStart={startDrag}
          onTouchMove={moveDrag}
          onTouchEnd={endDrag}
        >
          {rect && rect.w > 0 && rect.h > 0 && (
            <div
              className="absolute border-2 border-primary bg-primary/10 pointer-events-none"
              style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
            />
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-3">
        <button
          type="button"
          onClick={extractFromSelection}
          disabled={!rect || rect.w < 10 || rect.h < 10 || extracting}
          className="btn btn-primary btn-sm"
        >
          ✂ {t('settings_signature.extract_btn')}
        </button>
        {extractedPreview && (
          <div className="flex items-center gap-2 border border-border rounded-md p-1 bg-gray-50">
            <img src={extractedPreview} alt="" className="max-h-12" />
            <span className="text-[11px] text-success">✓ {t('settings_signature.extracted')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function mousePos(
  e: React.MouseEvent | React.TouchEvent,
  el: HTMLElement | null,
): { x: number; y: number } | null {
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  const isTouch = 'touches' in e;
  const cx = isTouch ? e.touches[0]?.clientX : e.clientX;
  const cy = isTouch ? e.touches[0]?.clientY : e.clientY;
  if (cx === undefined || cy === undefined) return null;
  return { x: cx - rect.left, y: cy - rect.top };
}
